import React, {
	useRef,
	useState,
	useEffect,
	useCallback,
	useContext
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { setClassNames } from '@ddc/classnames-js';
import { useLabels, useRequestData } from 'wsm-common-data';
import { trackEvent } from 'wsm-specials-display';
import {
	isProgramAspectRatioAccount,
	getCarouselDisplayProps
} from '../../utilities/aspect';
import { aR as ASPECT_RATIO } from '../../global-constants';
import { useVideoCarousel } from '../../hooks/useVideoCarousel';
import 'slick-carousel/slick/slick.scss';
import Promo from '../Promo/Promo';
import CarouselControls from './CarouselControls';
import { setListingsCarouselSlidesCondition } from '../../actions/generalState';
import { shouldShowDetailsButton } from '../../utilities/general';
import { PrefsContext } from '../../contexts';

const normalizeAssetUrl = (url = '') =>
	typeof url === 'string' ? url.split('|')[0].trim() : '';

const isVideoFileUrl = (url = '') =>
	/\.(mp4|webm|ogg|ogv|mov)(\?.*)?$/i.test(normalizeAssetUrl(url));

const isVideoXmlUrl = (url = '') =>
	/\.xml(\?.*)?$/i.test(normalizeAssetUrl(url));

const Carousel = ({
	isMobile = false,
	promos,
	width,
	isCouponCaro = false
}) => {
	const {
		aspectRatio,
		enableAppleWallet,
		enablePrint,
		autoplay,
		autoplaySpeed,
		showSlideshowIndicatorDots
	} = useContext(PrefsContext);

	const sliderRef = useRef();
	const dispatch = useDispatch();
	// The first slide is always viewed
	const [viewedSlides, setViewedSlides] = useState([0]);
	const labels = useLabels();
	const numSlides = promos.length;
	const { widgetName, windowId } = useRequestData();
	const [visibleSlideIndex, setVisibleSlideIndex] = useState(0);
	const [isSlideVisible, setIsSlideVisible] = useState(false);
	const [isSliderReady, setIsSliderReady] = useState(false);
	const [promoOutsiderButton, setPromoOutsiderButton] = useState(null);
	const account = useSelector((state) => state.account);
	const isSlideshow =
		aspectRatio === ASPECT_RATIO.DESKTOP_4X1 ||
		aspectRatio === ASPECT_RATIO.DESKTOP_10X1 ||
		aspectRatio === ASPECT_RATIO.MOBILE_4X3_SLIDESHOW ||
		aspectRatio === ASPECT_RATIO.MOBILE_4X3_COUPON;
	const programAspectRatioAccount = isProgramAspectRatioAccount(account);
	const isAdaptiveHeightEnabled =
		isSlideshow &&
		(programAspectRatioAccount === 'bmw' ||
			programAspectRatioAccount === 'mini');
	const isTallVerticalCoupon =
		aspectRatio === ASPECT_RATIO.DESKTOP_9X16 ||
		aspectRatio === ASPECT_RATIO.MOBILE_9X16;
	// Desktop 4x3 behaves like 9x16 (listing style with multi-video visible)
	// Mobile 4x3 coupon is handled in isSlideshow (auto-advance like 4x1)
	const isFourByThreeCoupon = aspectRatio === ASPECT_RATIO.DESKTOP_4X3;

	// Have customAssets and shouldShowDetailsButton = true => push down the control carousel button
	const isShowOfferDetailsCustomAsset =
		aspectRatio === ASPECT_RATIO.DESKTOP_10X1
			? promos.some(
					(promo) =>
						promo?.customAssets &&
						promo?.customAssets[ASPECT_RATIO.DESKTOP_10X1] &&
						shouldShowDetailsButton(
							promo,
							promo.incentiveData,
							aspectRatio,
							enableAppleWallet,
							enablePrint
						)
			  )
			: false;

	// Respect OS-level reduced-motion preference (WCAG 2.2.2 / 2.3.3).
	const prefersReducedMotion =
		typeof window !== 'undefined' &&
		window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ===
			true;

	// Set up autoplay state
	const [autoplaySlides, setAutoplaySlides] = useState(!prefersReducedMotion);
	const hasUserToggledAutoplayRef = useRef(false);
	// State to ignore infinitely wrap around contents to fix slide arrow make the special blank
	const [infiniteFlag, setInfiniteFlag] = useState(true);

	useEffect(() => {
		// Sync from prefs only until user explicitly toggles play/pause.
		// This prevents late preference updates from interrupting active playback.
		if (hasUserToggledAutoplayRef.current) {
			return;
		}
		// Don't auto-start when the OS prefers reduced motion.
		if (autoplay === 'true' && !prefersReducedMotion) {
			setAutoplaySlides(true);
		}
	}, [autoplay, prefersReducedMotion]);

	useEffect(() => {
		document?.addEventListener('keydown', (keyCode) => {
			if (keyCode.key === 'Tab') {
				setInfiniteFlag(false);
			} else {
				setInfiniteFlag(true);
			}
		});

		if (isCouponCaro && promos.length > 2) {
			dispatch(
				setListingsCarouselSlidesCondition({
					isCouponCaroFullSlide: true
				})
			);
		}
		/* eslint-disable react-hooks/exhaustive-deps */
	}, [isCouponCaro, promos.length]);

	// Detect video slides for slideshow and tall vertical aspect ratios
	const hasVideoSlides =
		(isSlideshow || isTallVerticalCoupon || isFourByThreeCoupon) &&
		promos.some((promo) => {
			const videoKey = `${aspectRatio}_video`;
			// Check all possible locations where video data might be stored
			const assetCandidates = [
				promo?.customAssetVideoForms?.[videoKey],
				promo?.customAssetVideoForms?.[aspectRatio],
				promo?.customAssets?.[videoKey],
				promo?.customAssets?.[aspectRatio],
				promo?.customAssetVideos?.[videoKey],
				promo?.customAssetVideos?.[aspectRatio]
			];

			return assetCandidates.some((candidate) => {
				if (!candidate) return false;

				if (typeof candidate === 'object') {
					const objectVideoUrls = [
						candidate.highestUrl,
						candidate.xmlUrl,
						...(Array.isArray(candidate.sources)
							? candidate.sources.map((source) => source?.url)
							: [])
					];

					return objectVideoUrls.some(
						(url) =>
							url && (isVideoFileUrl(url) || isVideoXmlUrl(url))
					);
				}

				return isVideoFileUrl(candidate) || isVideoXmlUrl(candidate);
			});
		});

	const slideHasVideo = useCallback(
		(promo) => {
			if (!promo) return false;

			const videoKey = `${aspectRatio}_video`;
			// Check all possible locations where video data might be stored
			const assetCandidates = [
				promo?.customAssetVideoForms?.[videoKey],
				promo?.customAssetVideoForms?.[aspectRatio],
				promo?.customAssets?.[videoKey],
				promo?.customAssets?.[aspectRatio],
				promo?.customAssetVideos?.[videoKey],
				promo?.customAssetVideos?.[aspectRatio]
			];

			return assetCandidates.some((candidate) => {
				if (!candidate) return false;

				if (typeof candidate === 'object') {
					const objectVideoUrls = [
						candidate.highestUrl,
						candidate.xmlUrl,
						...(Array.isArray(candidate.sources)
							? candidate.sources.map((source) => source?.url)
							: [])
					];

					return objectVideoUrls.some(
						(url) =>
							url && (isVideoFileUrl(url) || isVideoXmlUrl(url))
					);
				}

				return isVideoFileUrl(candidate) || isVideoXmlUrl(candidate);
			});
		},
		[aspectRatio]
	);

	const currentSlideHasVideo =
		(isSlideshow || isTallVerticalCoupon || isFourByThreeCoupon) &&
		slideHasVideo(promos[visibleSlideIndex]);

	const isSingleVideoSlide =
		(isSlideshow || isTallVerticalCoupon) &&
		numSlides === 1 &&
		hasVideoSlides;

	// Callback to advance to next slide (for video carousel coordination)
	const onAdvanceSlide = useCallback(() => {
		if (!sliderRef.current || numSlides <= 1) {
			return;
		}

		if (visibleSlideIndex >= numSlides - 1) {
			sliderRef.current.slickGoTo(0);
			return;
		}

		sliderRef.current.slickNext();
	}, [numSlides, visibleSlideIndex]);

	// Initialize video carousel hook only for slideshow placements.
	// 4x3/9x16 listing-style carousels can display multiple promos at once,
	// so videos in active cards are controlled together by the play/pause state.
	const enableVideoCarousel = isSlideshow && hasVideoSlides;
	// For multi-item layouts (Desktop 9x16, Desktop 4x3), videos always play regardless
	// of the play/pause button. The button only controls carousel auto-advance.
	// Mobile 9x16 shows only 1 item at a time, so it should pause videos with the button.
	const keepTallVerticalVideosPlaying =
		aspectRatio === ASPECT_RATIO.DESKTOP_9X16 ||
		aspectRatio === ASPECT_RATIO.DESKTOP_4X3;
	const shouldUseVideoPlaybackControl =
		enableVideoCarousel && currentSlideHasVideo;
	// Navigation arrows are always shown for 9x16 carousels; isMobile already
	// hides them on small viewports via CarouselControls' own guard.
	const hideNavigationForTallVerticalVideo = false;

	useEffect(() => {
		// For video slides, playback and advancing are driven by useVideoCarousel.
		// Disable Slick's internal autoplay so it cannot interrupt video playback.
		if (!sliderRef.current || !isSliderReady) return;

		if (shouldUseVideoPlaybackControl) {
			sliderRef.current.slickPause();
			return;
		}

		if (autoplaySlides) {
			sliderRef.current.slickPlay();
		} else {
			sliderRef.current.slickPause();
		}
	}, [autoplaySlides, shouldUseVideoPlaybackControl, isSliderReady]);

	const onSingleVideoEnd = useCallback(() => {
		hasUserToggledAutoplayRef.current = true;
		setAutoplaySlides(false);
	}, []);

	useVideoCarousel({
		promos,
		visibleSlideIndex,
		autoplaySlides: autoplaySlides && shouldUseVideoPlaybackControl,
		autoplaySpeed: parseInt(autoplaySpeed, 10) || 5000,
		onAdvanceSlide,
		isSingleVideoSlide,
		currentSlideHasVideo,
		isCurrentSlideVisible: isSlideVisible,
		sliderRef,
		onSingleVideoEnd
	});

	useEffect(() => {
		if (
			!sliderRef.current ||
			!isSliderReady ||
			!hasVideoSlides ||
			enableVideoCarousel ||
			!isSlideVisible
		) {
			return () => {};
		}

		const listElement = sliderRef.current?.innerSlider?.list;
		if (!listElement) {
			return () => {};
		}

		const cleanupCanPlayListeners = [];
		let mountRetryTimer = null;
		let delayedSyncTimer = null;

		const playWhenReady = (video) => {
			const currentVideo = video;
			currentVideo.muted = true;
			currentVideo.loop = true;

			if (currentVideo.ended) {
				currentVideo.currentTime = 0;
			}

			const tryPlay = () => {
				currentVideo.play().catch(() => {
					// Ignore browser autoplay restrictions.
				});
			};

			if (currentVideo.readyState >= 2) {
				tryPlay();
				return;
			}

			const handleCanPlay = () => {
				tryPlay();
			};

			currentVideo.addEventListener('canplay', handleCanPlay);
			cleanupCanPlayListeners.push(() => {
				currentVideo.removeEventListener('canplay', handleCanPlay);
			});
		};

		const allVideos = Array.from(
			listElement.querySelectorAll('video[data-is-video="true"]')
		);
		const activeVideos = new Set(
			Array.from(
				listElement.querySelectorAll(
					'.slick-slide.slick-active video[data-is-video="true"]'
				)
			)
		);
		const shouldForcePlay = autoplaySlides || keepTallVerticalVideosPlaying;

		allVideos.forEach((video) => {
			const currentVideo = video;
			if (!activeVideos.has(video)) {
				currentVideo.pause();
				return;
			}

			if (!shouldForcePlay) {
				currentVideo.pause();
				return;
			}

			playWhenReady(currentVideo);
		});

		if (shouldForcePlay && activeVideos.size === 0) {
			mountRetryTimer = setInterval(() => {
				const retryActiveVideos = Array.from(
					listElement.querySelectorAll(
						'.slick-slide.slick-active video[data-is-video="true"]'
					)
				);

				if (retryActiveVideos.length === 0) {
					return;
				}

				retryActiveVideos.forEach((video) => {
					playWhenReady(video);
				});

				clearInterval(mountRetryTimer);
				mountRetryTimer = null;
			}, 120);
		}

		if (shouldForcePlay) {
			delayedSyncTimer = setTimeout(() => {
				const delayedActiveVideos = Array.from(
					listElement.querySelectorAll(
						'.slick-slide.slick-active video[data-is-video="true"]'
					)
				);

				delayedActiveVideos.forEach((video) => {
					playWhenReady(video);
				});
			}, 220);
		}

		return () => {
			if (mountRetryTimer) {
				clearInterval(mountRetryTimer);
			}
			if (delayedSyncTimer) {
				clearTimeout(delayedSyncTimer);
			}
			cleanupCanPlayListeners.forEach((cleanup) => cleanup());
		};
	}, [
		autoplaySlides,
		enableVideoCarousel,
		hasVideoSlides,
		isSliderReady,
		isSlideVisible,
		keepTallVerticalVideosPlaying,
		visibleSlideIndex
	]);

	// Override Slick's default values with our own
	// See https://react-slick.neostack.com/docs/api/ for values
	const slickDefaults = {
		dots: true,
		infinite: false,
		dotsClass: setClassNames([
			isCouponCaro ? 'mt-3' : 'absolute-bottom',
			!isMobile ? 'on-desktop' : '',
			isCouponCaro ? 'coupon-carousel-controls' : '',
			'carousel-indicator-list',
			'dots',
			'infinite-dots',
			'w-100',
			'd-flex',
			'justify-content-center',
			isShowOfferDetailsCustomAsset ? 'isShowOfferDetailsCustomAsset' : ''
		]),
		arrows: !isMobile,
		adaptiveHeight: isAdaptiveHeightEnabled
	};

	// for 4x3 coupon carousel
	const couponCaroProps = {
		slidesToShow: 3,
		slidesToScroll: 3,
		responsive: [
			{
				breakpoint: 992,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 2
				}
			}
		]
	};

	// for tall vertical carousel
	const tallVerticalCouponCaroProps = {
		slidesToShow: 4,
		slidesToScroll: 4,
		responsive: [
			{
				breakpoint: 992,
				settings: {
					slidesToShow: 3,
					slidesToScroll: 3
				}
			},
			{
				breakpoint: 764,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 2
				}
			}
		]
	};

	const getCarouselProps = () => {
		if (!isCouponCaro) return {};

		// For mobile, always show 1 slide regardless of carousel type
		if (isMobile) {
			return {
				slidesToShow: 1,
				slidesToScroll: 1
			};
		}

		const baseProps = isTallVerticalCoupon
			? tallVerticalCouponCaroProps
			: couponCaroProps;

		// Ensure slidesToShow never exceeds the number of available promos
		const maxSlides = promos.length;
		const adjustedProps = {
			...baseProps,
			slidesToShow: Math.min(baseProps.slidesToShow, maxSlides),
			slidesToScroll: Math.min(baseProps.slidesToScroll, maxSlides)
		};

		// Also adjust responsive breakpoint settings
		if (adjustedProps.responsive) {
			adjustedProps.responsive = adjustedProps.responsive.map(
				(breakpoint) => ({
					...breakpoint,
					settings: {
						...breakpoint.settings,
						slidesToShow: Math.min(
							breakpoint.settings.slidesToShow,
							maxSlides
						),
						slidesToScroll: Math.min(
							breakpoint.settings.slidesToScroll,
							maxSlides
						)
					}
				})
			);
		}

		return adjustedProps;
	};

	const caroProps = getCarouselDisplayProps(
		isCouponCaro,
		isMobile,
		isTallVerticalCoupon,
		width
	);

	// Use media query setting for reduced motion (selectable in OS)
	const reducedMotionMatch =
		typeof window !== 'undefined' &&
		window.matchMedia('(prefers-reduced-motion: reduce)');

	// The following settings are enforced for accessibility
	const accessibilityOverrides = {
		accessibility: true,
		draggable: false, // Desktop only
		pauseOnFocus: false,
		pauseOnHover: false,
		fade: reducedMotionMatch && reducedMotionMatch.matches,
		infinite: infiniteFlag
	};

	const setAriaLive = useCallback(
		(disable) => {
			// Add carousel ARIA attributes to the elements dynamically created by Slick
			// ARIA specifications for carousels: https://www.w3.org/TR/wai-aria-practices/#carousel
			if (sliderRef && sliderRef.current) {
				const slideContainer =
					sliderRef.current.innerSlider.list.querySelector(
						'.slick-track'
					);

				if (slideContainer) {
					// Add aria attributes to the parent element of the slides
					slideContainer.setAttribute('aria-atomic', 'true');
					slideContainer.setAttribute(
						'aria-live',
						autoplaySlides || disable ? 'off' : 'polite'
					);
				}
			}
		},
		[autoplaySlides]
	);

	const setTabIndexForActiveSlide = () => {
		if (!sliderRef || !sliderRef.current) {
			return;
		}
		const listEl = sliderRef.current.innerSlider.list;
		if (!listEl) return;

		// Make all focusable elements inside hidden slides unreachable
		const allSlides = listEl.querySelectorAll('.slick-slide');
		allSlides.forEach((slide) => {
			const isActive = slide.classList.contains('slick-active');
			slide.setAttribute('tabIndex', isActive ? '0' : '-1');
			const focusableEls = slide.querySelectorAll(
				'a, input, button, select, [tabindex]'
			);
			focusableEls.forEach((el) => {
				el.setAttribute('tabIndex', isActive ? '0' : '-1');
			});
		});
	};

	useEffect(() => {
		if (isMobile) {
			const handleDragStart = (e) => {
				// Touch or mouse's primary button
				if (e.type !== 'mousedown' || e.button === 0) {
					if (!enableVideoCarousel) {
						setAutoplaySlides(false);
					}
				}
			};
			const slideContainer =
				sliderRef.current.innerSlider.list.querySelector(
					'.slick-track'
				);

			slideContainer.addEventListener(
				'mousedown',
				handleDragStart,
				false
			);
			slideContainer.addEventListener(
				'touchstart',
				handleDragStart,
				false
			);

			return () => {
				slideContainer.removeEventListener(
					'mousedown',
					handleDragStart,
					false
				);
				slideContainer.removeEventListener(
					'touchstart',
					handleDragStart,
					false
				);
			};
		}
		return () => {};
	}, [sliderRef, isMobile, enableVideoCarousel]);

	// Once user interacts with slide controls, stop autoplay
	const onFocus = () => {
		if (!enableVideoCarousel) {
			setAutoplaySlides(false);
		}
	};

	const trackInteraction = (swipe) => {
		let element;
		let result;
		const { oemId, title } = promos[visibleSlideIndex];
		if (swipe) {
			element =
				swipe === 'left'
					? 'Carousel next slide button'
					: 'Carousel previous button';
			result =
				swipe === 'left'
					? `Moved to carousel item ${visibleSlideIndex}`
					: `Move to previous photo ${visibleSlideIndex}`;
		} else {
			element = autoplaySlides
				? 'Carousel pause button'
				: 'Carousel play button';
			result = autoplaySlides ? 'Carousel paused' : 'Carousel played';
		}

		trackEvent({
			widgetName,
			windowId,
			element,
			result,
			dataToExcludeFromWidgetUserEvent: {
				title,
				oemId,
				index: visibleSlideIndex,
				count: promos.length,
				enableCarousel: true
			}
		});
	};

	const onSwipe = (swipeDirection) => {
		if (promoOutsiderButton !== null) setPromoOutsiderButton(null);
		// This event happens after slide has been pushed into place, but before
		//  the beforeChange() callback.
		// Always pause autoplay when user swipes to give them control.
		setAutoplaySlides(false);

		// For slideshow modes (single item visible), also pause videos immediately.
		// For listing modes (multiple items visible), videos keep playing - only
		// the carousel auto-advance is paused.
		// This includes: 4x1, Mobile 4x3 slideshow/coupon, Mobile 9x16 (all show 1 item)
		const shouldPauseVideosOnSwipe =
			(enableVideoCarousel || aspectRatio === ASPECT_RATIO.MOBILE_9X16) &&
			!keepTallVerticalVideosPlaying;
		if (shouldPauseVideosOnSwipe) {
			const listElement = sliderRef.current?.innerSlider?.list;
			if (listElement) {
				const activeVideos = listElement.querySelectorAll(
					'.slick-slide.slick-active video[data-is-video="true"]'
				);
				activeVideos.forEach((video) => {
					video.pause();
				});
			}
		}

		trackInteraction(swipeDirection);
	};

	const onNext = () => {
		if (infiniteFlag === false) setInfiniteFlag(true);
		if (promoOutsiderButton !== null) setPromoOutsiderButton(null);
		setAutoplaySlides(false);
		sliderRef.current.slickNext();
		trackInteraction('left');
	};

	const onPrev = () => {
		if (infiniteFlag === false) setInfiniteFlag(true);
		if (promoOutsiderButton !== null) setPromoOutsiderButton(null);
		setAutoplaySlides(false);
		sliderRef.current.slickPrev();
		trackInteraction('right');
	};

	const onPlayPause = () => {
		if (infiniteFlag === false) setInfiniteFlag(true);
		hasUserToggledAutoplayRef.current = true;
		setAutoplaySlides(!autoplaySlides);
		trackInteraction();
	};

	const ensureActiveSlideVideoPlayback = useCallback(() => {
		if (!enableVideoCarousel || !autoplaySlides || !sliderRef.current) {
			return;
		}

		const listElement = sliderRef.current?.innerSlider?.list;
		if (!listElement) {
			return;
		}

		const activeVideo = listElement.querySelector(
			'.slick-slide.slick-active video[data-is-video="true"]'
		);
		if (!activeVideo) {
			return;
		}

		const currentVideo = activeVideo;
		if (currentVideo.ended) {
			currentVideo.currentTime = 0;
		}

		const playVideo = () => {
			currentVideo.play().catch(() => {
				// Ignore browser autoplay restrictions.
			});
		};

		if (currentVideo.readyState >= 2) {
			playVideo();
			return;
		}

		const handleCanPlay = () => {
			playVideo();
		};

		currentVideo.addEventListener('canplay', handleCanPlay, {
			once: true
		});
	}, [autoplaySlides, enableVideoCarousel]);

	// Load slide content for current index, two slides ahead, and one slide behind
	const loadPromoContents = (slideIndex) => {
		const oneAheadIndex = (visibleSlideIndex + 1) % numSlides;
		const twoAheadIndex = (visibleSlideIndex + 2) % numSlides;
		const oneBehindIndex = (visibleSlideIndex - 1 + numSlides) % numSlides;

		if (
			[
				visibleSlideIndex,
				oneAheadIndex,
				isCouponCaro ? twoAheadIndex : oneBehindIndex
			].includes(slideIndex) ||
			isCouponCaro
		) {
			setIsSlideVisible(true);
		}
	};

	return (
		<section
			aria-live="polite"
			aria-roledescription="carousel"
			aria-label={labels.get('SPECIAL_OFFERS')}
			data-testid={isCouponCaro ? 'coupon-carousel' : 'regular-carousel'}
		>
			<Slider
				{...slickDefaults}
				{...getCarouselProps()}
				{...accessibilityOverrides}
				autoplay={
					autoplaySlides &&
					(!isCouponCaro || isTallVerticalCoupon) &&
					(!enableVideoCarousel || !currentSlideHasVideo)
				}
				autoplaySpeed={parseInt(autoplaySpeed, 10) || 5000}
				afterChange={(slideIndex) => {
					if (viewedSlides.indexOf(slideIndex) === -1) {
						viewedSlides.push(slideIndex);
						setViewedSlides(viewedSlides);
					}
					setTabIndexForActiveSlide();
					setTimeout(() => {
						ensureActiveSlideVideoPlayback();
					}, 0);
				}}
				beforeChange={(_currentSlide, _nextSlide) => {
					setVisibleSlideIndex(_nextSlide);
					setPromoOutsiderButton(null);
				}}
				className="carousel hide-buttons"
				onInit={() => {
					setIsSliderReady(true);
					setTabIndexForActiveSlide();
					loadPromoContents(0);
				}}
				onReInit={() => {
					setIsSliderReady(true);
					setTabIndexForActiveSlide();
					setAriaLive();
				}}
				onSwipe={(swipe) => onSwipe(swipe)}
				appendDots={(dots) =>
					CarouselControls({
						dots,
						visibleSlideIndex,
						belowPosition:
							isMobile ||
							aspectRatio === ASPECT_RATIO.DESKTOP_10X1 ||
							isCouponCaro,
						onPlayPause,
						onPrev,
						onNext,
						onFocus,
						autoplaySlides,
						aspectRatio,
						isMobile,
						labels,
						caroProps,
						promosLength: promos.length,
						isCouponCaro,
						hideNavigation: hideNavigationForTallVerticalVideo,
						showSlideshowIndicatorDots
					})
				}
				ref={sliderRef}
			>
				{promos.map((promo, i) => (
					<Promo
						key={promo.id}
						isMobile={isMobile}
						isCurrent={visibleSlideIndex === i}
						promoIndex={i}
						isVisible={isSlideVisible}
						promo={promo}
						positionLabel={`${i + 1} ${labels.get('OF')} ${
							promos.length
						}`}
						role="group"
						roleDescription="slide"
					/>
				))}
			</Slider>
			{/* Single video slide play/pause button - shown when dots don't render */}
			{isSingleVideoSlide && (
				<div
					className={setClassNames([
						'absolute-bottom',
						isMobile ? '' : 'on-desktop',
						'carousel-indicator-list',
						'dots',
						'w-100',
						'd-flex',
						'justify-content-end'
					])}
				>
					<div
						id="controls-container"
						role="region"
						aria-label={labels.get('CAROUSEL_CONTROLS')}
					>
						<div className="bottom-controls">
							<ul aria-label="bottom-slide-index" />
							<button
								type="button"
								className={setClassNames([
									isMobile ? 'pause-play-button-bottom' : '',
									'pause-play-button',
									'bg-transparent'
								])}
								onClick={onPlayPause}
								aria-label={
									autoplaySlides
										? labels.get('STOP_SLIDE_ROTATION')
										: labels.get('START_SLIDE_ROTATION')
								}
							>
								<i
									role="presentation"
									aria-hidden="true"
									className={setClassNames([
										'd-inline-block',
										'ddc-icon',
										'font-weight-bold',
										'rounded-circle',
										autoplaySlides
											? 'ddc-icon-pause'
											: 'ddc-icon-play',
										isMobile ? 'icon-dimension-size-4' : ''
									])}
								/>
							</button>
						</div>
					</div>
				</div>
			)}
		</section>
	);
};

Carousel.propTypes = {
	isMobile: PropTypes.bool,
	promos: PropTypes.arrayOf(
		PropTypes.shape({
			type: PropTypes.string.isRequired,
			id: PropTypes.string.isRequired,
			autoGenerated: PropTypes.bool.isRequired,
			customAssets: PropTypes.shape({}),
			oemId: PropTypes.string,
			title: PropTypes.string
		})
	).isRequired,
	slickProps: PropTypes.shape({}),
	width: PropTypes.number,
	isCouponCaro: PropTypes.bool
};

export default Carousel;
